import { Component, ChangeDetectorRef } from '@angular/core';
import { PanAppState, PlatformService, UserState } from '@panamax/app-state';
import { CustomerReportingFilterService } from '../../services/customer-reporting-filter.service';
import { CustomerReportingModalService } from '../../services/customer-reporting-modal.service';
import {
  Observable,
  Subscription,
  Subject,
  takeUntil,
  filter,
  take,
} from 'rxjs';
import { ReportTableColumn } from '@app/customer-reporting/models/report-table-column.model';
import { ViewDidEnter, ViewWillEnter, ViewWillLeave } from '@ionic/angular';
import { ProductUsageTable } from '@app/customer-reporting/constants/product-usage-table-config';
import { CustomerReportingToastService } from '@app/customer-reporting/services/customer-reporting-toast.service';
import { Store } from '@ngrx/store';
import {
  CUSTOMER_REPORTING_ACTIONS,
  ICallerInformation,
  IReportFilters,
  selectIsTableLoading,
  selectTableReport,
  selectTableReportError,
} from '@usf/ngrx-customer-reporting';
import { formatDate } from '@app/customer-reporting/utils/date-range.util';
import { groupCustomersByDivision } from '@app/customer-reporting/utils/customers.util';
import { CustomerReportingCsvService } from '../../services/customer-reporting-csv.service';

@Component({
  selector: 'app-product-usage',
  templateUrl: './product-usage.page.html',
  styleUrls: ['./product-usage.page.scss'],
})
export class ProductUsagePageComponent
  implements ViewWillEnter, ViewDidEnter, ViewWillLeave
{
  deliveryDateRange$: Observable<string>;
  numberSelectedFilters$: Observable<number>;
  modalFilterSub: Subscription;
  destroyed$ = new Subject<void>();
  dataSource = [];
  noData = false;
  isTableLoading$: Observable<boolean>;
  tableErrorSubscription: Subscription;
  tableDataSubscription: Subscription;
  columnsToDisplay: ReportTableColumn[] = ProductUsageTable;
  submenuOpen = false;
  private currentUser: UserState;

  constructor(
    public platformService: PlatformService,
    readonly panAppState: PanAppState,
    private filterService: CustomerReportingFilterService,
    private customerReportingModal: CustomerReportingModalService,
    private cdr: ChangeDetectorRef,
    private toastService: CustomerReportingToastService,
    private store: Store,
    private customerReportingCsvService: CustomerReportingCsvService,
  ) {}

  get isDesktop(): boolean {
    return (
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.desktop
    );
  }

  get isTablet(): boolean {
    return (
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.tablet
    );
  }

  get isMobile(): boolean {
    return (
      this.platformService?.platformType ===
      this.platformService?.platformEnum?.mobile
    );
  }

  get isTouch(): boolean {
    return this.isTablet || this.isMobile;
  }

  get platformClass(): string {
    if (this.isDesktop) {
      return 'desktop';
    }
    if (this.isTablet) {
      return 'tablet';
    }
    if (this.isMobile) {
      return 'mobile';
    }
    return '';
  }

  ionViewWillEnter() {
    this.noData = true;
    this.panAppState.setPageTitle('Product Usage');
    this.deliveryDateRange$ = this.filterService.deliveryDateRange$;
    this.numberSelectedFilters$ = this.filterService.numberSelectedFilters$;
    this.modalFilterSub = this.customerReportingModal.filtersSelected
      .pipe(takeUntil(this.destroyed$))
      .subscribe(event => {
        this.filterService.onFilterChange(event);
        if (event?.data?.filters.length > 0 && event?.role !== 'backdrop') {
          this.getCsvAsJson();
        }
      });
    this.filterService.filtersUpdated$
      .pipe(
        filter(updated => updated),
        takeUntil(this.destroyed$),
      )
      .subscribe(() => {
        this.cdr.detectChanges();
      });

    this.isTableLoading$ = this.store.select(selectIsTableLoading);
    this.tableDataSubscription = this.store
      .select(selectTableReport)
      .pipe(filter(report => !!report))
      .subscribe(report => {
        this.dataSource = report.rows.slice(0, 10);

        if (this.dataSource.length === 0) {
          this.noData = true;
        } else {
          this.noData = false;
        }
      });
    this.tableErrorSubscription = this.store
      .select(selectTableReportError)
      .pipe(filter(err => !!err))
      .subscribe(err => {
        this.noData = true;
        this.toastService.show(
          'Product Usage',
          'apierror',
          0,
          true,
          err.errorCode,
        );
      });
  }

  ionViewDidEnter() {
    // Get table data when page is loaded
    this.panAppState.user$
      .pipe(
        filter(
          user =>
            !!user && !!user.userId && !!user.firstName && !!user.lastName,
        ),
        take(1),
      )
      .subscribe(user => {
        this.currentUser = user;
        this.getCsvAsJson();
      });
  }

  // toggle from the Download button
  toggleMenu(): void {
    this.submenuOpen = !this.submenuOpen;
  }

  // click on a menu option
  selectReport(): void {
    this.downloadFullProductUsageReportCsv();
    this.submenuOpen = false;
  }

  private downloadFullProductUsageReportCsv(): void {
    if (this.noData) {
      this.toastService.show('Product Usage Report', 'nodataerror');
      return;
    }

    if (!this.currentUser) {
      this.toastService.show(
        'Product Usage Report',
        'apierror',
        0,
        true,
        'USER_INFO_MISSING',
      );
      return;
    }

    const callerInfo: ICallerInformation = {
      userName: this.currentUser.userId,
      firstName: this.currentUser.firstName,
      lastName: this.currentUser.lastName,
    };

    const reportFilters: IReportFilters = {
      limit: 100000, // High limit for "full" report
      invoiceDate: {
        start: formatDate(this.filterService.fromDate),
        end: formatDate(this.filterService.toDate),
      },
      divisions: groupCustomersByDivision(this.filterService.selectedCustomers),
      sortBy: 'Dollars',
    };

    this.customerReportingCsvService.downloadCsv(
      callerInfo,
      'SP_QUERY_PRODUCT_DETAIL_DATA',
      reportFilters,
    );
  }

  openModal(quickDate: boolean = false) {
    this.customerReportingModal.open(
      this.filterService.fromDate,
      this.filterService.toDate,
      this.filterService.selectedCustomers,
      quickDate,
    );
  }

  resetFilters(fromDestroy: boolean = false) {
    this.filterService.resetFilters();
  }

  ionViewWillLeave(): void {
    this.noData = false;
    this.dataSource = [];
    this.toastService.clearToasts();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.store.dispatch(CUSTOMER_REPORTING_ACTIONS.resetTableReportState());

    if (this.tableDataSubscription) {
      this.tableDataSubscription.unsubscribe();
    }
    if (this.tableErrorSubscription) {
      this.tableErrorSubscription.unsubscribe();
    }
  }

  getCsvAsJson() {
    this.store.dispatch(
      CUSTOMER_REPORTING_ACTIONS.loadTableReport({
        storedProcedure: 'SP_QUERY_PRODUCT_DETAIL_DATA',
        filters: {
          limit: 10,
          invoiceDate: {
            start: formatDate(this.filterService.fromDate),
            end: formatDate(this.filterService.toDate),
          },
          divisions: groupCustomersByDivision(
            this.filterService.selectedCustomers,
          ),
          sortBy: 'Dollars',
        },
      }),
    );
  }
}
